// pages/MyLeaveAndTILPage.tsx
// View-only page for logged-in staff to see annual leave and TIL summary.
// FIXED: Now shows annualLeaveCurrentYear (actual allocation for THIS year) instead of contractual total

import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Divider,
  Tabs,
  Tab,
  Stack,
  Paper,
} from '@mui/material';
import {
  BeachAccess as AnnualLeaveIcon,
  AccessTime as TimeIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';

interface LeaveBalance {
  annualLeave: number;
  timeInLieu: number;
  timeInLieuDays: number;
  totalAvailableDays: number;
  entitlement?: {
    annualLeaveTotal: number;           // Contractual entitlement
    annualLeaveCurrentYear?: number;    // Actual allocation for THIS year
    annualLeaveUsed: number;
    annualLeaveYear?: number;
    yearStartDate?: string;
    yearEndDate?: string;
  };
}

interface LeaveRequest {
  _id: string;
  type: 'annual-leave' | 'time-lieu';
  dateStart: string;
  dateEnd?: string;
  hoursRequested?: number;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  decisionNote?: string;
}

interface TILEntry {
  hours: number;
  earnedDate: string;
  expiryDate: string;
  description: string;
  status: 'expired' | 'expiring-soon' | 'active';
}

interface RotaLeaveDay { 
  date: string; 
  status: string; 
}

type StatusFilter = 'all' | 'approved' | 'pending' | 'rejected' | 'cancelled';

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString('en-IE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

const formatWeekday = (date: string) =>
  new Date(date).toLocaleDateString('en-IE', { weekday: 'long' });

const MyLeaveAndTILPage: React.FC = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [personnelId, setPersonnelId] = useState<string | null>(null);
  const [balance, setBalance] = useState<LeaveBalance | null>(null);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [tilEntries, setTilEntries] = useState<TILEntry[]>([]);
  const [rotaLeaveDays, setRotaLeaveDays] = useState<RotaLeaveDay[]>([]);

  // Tabs / Filters
  const [alStatus, setAlStatus] = useState<StatusFilter>('all');
  const [tilStatus, setTilStatus] = useState<StatusFilter>('all');

  // --- Fetch personnel by user ---
  useEffect(() => {
    if (!user?._id) return;
    (async () => {
      try {
        const res = await axiosInstance.get(`/api/personnel/by-user/${user._id}`);
        setPersonnelId(res.data._id);
      } catch (err: any) {
        console.error('Error fetching personnel record:', err);
        setError('Unable to find your personnel record.');
        setLoading(false);
      }
    })();
  }, [user]);

  // --- Fetch all core data ---
  useEffect(() => {
    if (!personnelId) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const [balanceRes, requestsRes, tilRes] = await Promise.all([
          axiosInstance.get(`/api/personnel/${personnelId}/leave/balance`),
          axiosInstance.get('/api/leave-requests/my-requests'),
          axiosInstance.get(`/api/personnel/${personnelId}/leave/til-history`),
        ]);

        const fullBalance: LeaveBalance = {
          ...balanceRes.data.balance,
          entitlement: balanceRes.data.entitlement,
        };
        setBalance(fullBalance);
        setLeaveRequests(requestsRes.data);
        setTilEntries(tilRes.data.tilEntries);

        // Fetch rota-entered AL days within leave year
        const startISO = balanceRes.data.entitlement?.yearStartDate || `${new Date().getFullYear()}-01-01`;
        const endISO = balanceRes.data.entitlement?.yearEndDate || `${new Date().getFullYear()}-12-31`;
        const rotaRes = await axiosInstance.get(`/api/rota/person/${personnelId}/annual-leave-days?start=${startISO}&end=${endISO}`);
        setRotaLeaveDays(rotaRes.data.days || []);
      } catch (err: any) {
        console.error('Error loading leave data:', err);
        setError('Failed to load leave and TIL information.');
      } finally {
        setLoading(false);
      }
    })();
  }, [personnelId]);

  // --- Year window ---
  const yearStart = useMemo(() => {
    if (!balance?.entitlement?.yearStartDate) return new Date(new Date().getFullYear(), 0, 1);
    return new Date(balance.entitlement.yearStartDate);
  }, [balance]);

  const yearEnd = useMemo(() => {
    if (!balance?.entitlement?.yearEndDate) return new Date(new Date().getFullYear(), 11, 31);
    return new Date(balance.entitlement.yearEndDate);
  }, [balance]);

  // --- Headline numbers (FIXED: Use currentYear allocation, not contractual) ---
  const contractualEntitlement = balance?.entitlement?.annualLeaveTotal ?? 0;
  const currentYearAllocation = balance?.entitlement?.annualLeaveCurrentYear ?? contractualEntitlement;
  const usedAnnualLeave = balance?.entitlement?.annualLeaveUsed ?? 0;
  const remainingAnnualLeave = balance?.annualLeave ?? 0;
  const isProRata = currentYearAllocation !== contractualEntitlement;

  const totalTILEarnedThisYear = useMemo(() => {
    const start = yearStart.getTime();
    const end = yearEnd.getTime();
    return tilEntries.reduce((sum, e) => {
      const t = new Date(e.earnedDate).getTime();
      return t >= start && t <= end ? sum + e.hours : sum;
    }, 0);
  }, [tilEntries, yearStart, yearEnd]);

  // --- Split requests ---
  const alRequests = useMemo(() => leaveRequests.filter(r => r.type === 'annual-leave'), [leaveRequests]);
  const tilRequestsOnly = useMemo(() => leaveRequests.filter(r => r.type === 'time-lieu'), [leaveRequests]);

  const applyStatusFilter = (items: LeaveRequest[], status: StatusFilter) =>
    status === 'all' ? items : items.filter(i => i.status === status);

  const alFiltered = applyStatusFilter(alRequests, alStatus);
  const tilFiltered = applyStatusFilter(tilRequestsOnly, tilStatus);

  // --- Build approved AL days list from requests + rota entries ---
  const approvedALDaysThisYear = useMemo(() => {
    const daysFromReq: string[] = [];

    alRequests
      .filter(r => r.status === 'approved')
      .forEach(r => {
        const start = new Date(r.dateStart);
        const end = new Date(r.dateEnd || r.dateStart);
        const cur = new Date(start);
        cur.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        while (cur.getTime() <= end.getTime()) {
          if (cur >= yearStart && cur <= yearEnd) {
            daysFromReq.push(cur.toISOString());
          }
          cur.setDate(cur.getDate() + 1);
        }
      });

    const daysFromRota = rotaLeaveDays
      .map(d => new Date(d.date))
      .filter(d => d >= yearStart && d <= yearEnd)
      .map(d => new Date(d.setHours(0, 0, 0, 0)).toISOString());

    const merged = Array.from(new Set([...daysFromReq, ...daysFromRota]));
    merged.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    return merged;
  }, [alRequests, rotaLeaveDays, yearStart, yearEnd]);

  // --- Helpers ---
  const durationLabel = (req: LeaveRequest) => {
    if (req.hoursRequested) return `${req.hoursRequested}h`;
    const start = new Date(req.dateStart);
    const end = new Date(req.dateEnd || req.dateStart);
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return `${days} day${days > 1 ? 's' : ''}`;
  };

  // --- UI ---
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading your leave and TIL summary...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const currentYear = balance?.entitlement?.annualLeaveYear || new Date().getFullYear();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          My Leave & Time In Lieu
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your leave entitlement and balances for the {currentYear} leave year ({format(yearStart, 'dd MMM yyyy')} - {format(yearEnd, 'dd MMM yyyy')})
        </Typography>
      </Box>

      {/* Pro-Rata Notice */}
      {isProRata && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Pro-Rata Allocation:</strong> Your contractual entitlement is {contractualEntitlement} days per year, 
            but you have been allocated <strong>{currentYearAllocation} days for the remainder of {currentYear}</strong> based on your start date.
          </Typography>
        </Alert>
      )}

      {/* Summary Cards */}
      {balance && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Annual Leave Card - FIXED */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <AnnualLeaveIcon color="primary" sx={{ fontSize: 48, mt: 1 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      Annual Leave ({currentYear})
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary.main" sx={{ my: 1 }}>
                      {remainingAnnualLeave} days remaining
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Allocated (this year):
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {currentYearAllocation} days
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Used:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {usedAnnualLeave} days
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Remaining:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {remainingAnnualLeave} days
                      </Typography>
                    </Box>
                    {isProRata && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'info.dark', borderRadius: 1 }}>
                        <Typography variant="caption" color="info.light">
                          ℹ️ Contractual entitlement: {contractualEntitlement} days/year
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Time in Lieu Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <TimeIcon color="info" sx={{ fontSize: 48, mt: 1 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      Time in Lieu
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="info.main" sx={{ my: 1 }}>
                      {balance.timeInLieu}h available
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Earned this year:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {totalTILEarnedThisYear}h
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Available balance:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {balance.timeInLieu}h
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Equivalent days:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        ~{balance.timeInLieuDays} days
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Annual Leave Requests */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h5">Annual Leave Requests</Typography>
          <Tabs value={alStatus} onChange={(_, v) => setAlStatus(v)} aria-label="AL status tabs">
            <Tab label="All" value="all" />
            <Tab label="Approved" value="approved" />
            <Tab label="Pending" value="pending" />
            <Tab label="Rejected" value="rejected" />
          </Tabs>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {alFiltered.length === 0 ? (
          <Alert severity="info">No annual leave requests for the selected filter.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date Start</TableCell>
                  <TableCell>Date End</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alFiltered.map((req) => (
                  <TableRow key={req._id}>
                    <TableCell>{format(new Date(req.dateStart), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>{req.dateEnd ? format(new Date(req.dateEnd), 'dd/MM/yyyy') : '-'}</TableCell>
                    <TableCell>{durationLabel(req)}</TableCell>
                    <TableCell>
                      <Chip
                        label={req.status}
                        size="small"
                        color={
                          req.status === 'approved' ? 'success' :
                          req.status === 'rejected' ? 'error' :
                          req.status === 'pending' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{req.reason || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Approved Annual Leave Days */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Approved Annual Leave Days — {currentYear}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {approvedALDaysThisYear.length === 0 ? (
          <Alert severity="info">No annual leave days recorded this year.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Weekday</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {approvedALDaysThisYear.map((date, index) => (
                  <TableRow key={index}>
                    <TableCell>{formatDate(date)}</TableCell>
                    <TableCell>{formatWeekday(date)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold' }}>
                    Total: {approvedALDaysThisYear.length} days
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* TIL Requests */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h5">Time In Lieu Requests</Typography>
          <Tabs value={tilStatus} onChange={(_, v) => setTilStatus(v)} aria-label="TIL status tabs">
            <Tab label="All" value="all" />
            <Tab label="Approved" value="approved" />
            <Tab label="Pending" value="pending" />
            <Tab label="Rejected" value="rejected" />
          </Tabs>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {tilFiltered.length === 0 ? (
          <Alert severity="info">No time in lieu requests for the selected filter.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tilFiltered.map((req) => (
                  <TableRow key={req._id}>
                    <TableCell>{format(new Date(req.dateStart), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>{req.hoursRequested ? `${req.hoursRequested}h` : '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={req.status}
                        size="small"
                        color={
                          req.status === 'approved' ? 'success' :
                          req.status === 'rejected' ? 'error' :
                          req.status === 'pending' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{req.reason || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* TIL History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>Time In Lieu History</Typography>
        <Divider sx={{ mb: 2 }} />
        {tilEntries.length === 0 ? (
          <Alert severity="info">No time in lieu entries recorded.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Earned</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tilEntries.map((entry, i) => (
                  <TableRow key={i}>
                    <TableCell>{format(new Date(entry.earnedDate), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>{format(new Date(entry.expiryDate), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell>{entry.hours}h</TableCell>
                    <TableCell>
                      <Chip
                        label={entry.status.replace('-', ' ')}
                        size="small"
                        color={entry.status === 'expired' ? 'error' : entry.status === 'expiring-soon' ? 'warning' : 'success'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default MyLeaveAndTILPage;